import React from 'react';
import { Box, Typography, TextField, FormControlLabel } from '@mui/material';
import InfoTooltip from '../../InfoTooltip';
import { useTheme } from '@mui/material/styles';
import Switch from '@mui/material/Switch';

export interface DownloadSettingsProps {
  download: 'NO' | 'YES';
  setDownload: (v: 'NO' | 'YES') => void;
  keystorePath: string;
  setKeystorePath: (v: string) => void;
  patchFolder: string;
  platform: string;
  downloadLocked: boolean;
  disabledByGoldImage?: boolean;
}

const DownloadSettings: React.FC<DownloadSettingsProps> = (props) => {
  const {
    download,
    setDownload,
    keystorePath,
    setKeystorePath,
    patchFolder,
    platform,
    downloadLocked,
    disabledByGoldImage,
  } = props;

  const theme = useTheme();
  const patchFolderDisplay = patchFolder || '/home/oracle/autoupgrade/patches';

  // Theme-adaptive colors for tooltip
  const checkedColor = theme.palette.mode === 'dark' ? '#2e7d4f' : '#43a047';
  const uncheckedColor = theme.palette.mode === 'dark' ? '#888' : '#bbb';

  // Determine platform for keystore placeholder
  const keystorePlaceholder = platform === 'WINDOWS.X64'
    ? 'C:\\Oracle\\AutoUpgrade\\Keystore'
    : '/home/oracle/autoupgrade/keystore';

  // Touched state for Keystore Path
  const [keystorePathTouched, setKeystorePathTouched] = React.useState(false);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontWeight: 600 }}>Download Patches:</Typography>
        <InfoTooltip
          title={
            <>
              <div style={{ marginBottom: 8, color: checkedColor, fontWeight: 500 }}>
                <b>When enabled:</b> AutoUpgrade will download the required patches into <b>{patchFolderDisplay}</b> if MOS credentials are properly setup. You need to save Oracle Database base image in same folder.
              </div>
              <div style={{ color: uncheckedColor, fontWeight: 500 }}>
                <b>When disabled:</b> You must manually download the patches and the Oracle Database base image, then save them into the <b>{patchFolderDisplay}</b>.
              </div>
            </>
          }
          placement="top"
          iconSx={{ ml: 1.5 }}
          maxWidth={1100}
        />
      </Box>
      <FormControlLabel
        control={
          <Switch
            checked={disabledByGoldImage ? false : downloadLocked ? true : download === 'YES'}
            onChange={e => setDownload(e.target.checked ? 'YES' : 'NO')}
            color="primary"
            disabled={downloadLocked || !!disabledByGoldImage}
          />
        }
        label={<Typography sx={{ fontWeight: 600 }}>Download</Typography>}
      />
      {!disabledByGoldImage && (download === 'YES' || downloadLocked) && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Keystore Path:</Typography>
          <TextField
            fullWidth
            size="small"
            value={keystorePath}
            onChange={e => { setKeystorePathTouched(true); setKeystorePath(e.target.value); }}
            onFocus={() => { if (!keystorePathTouched && keystorePath === '') setKeystorePath(''); setKeystorePathTouched(true); }}
            placeholder={keystorePlaceholder}
            InputProps={{
              sx: {
                '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                '& .MuiInputBase-input': {
                  color: !keystorePathTouched && keystorePath === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
                fontSize: 15,
                background: 'transparent',
              },
            }}
          />
        </Box>
      )}
      {/* Add any other settings here, always visible */}
    </Box>
  );
};

export default DownloadSettings; 